// ================================
// TRIAL SYSTEM CONFIG (Frontend)
// ================================
// 48-hour free trial for new accounts
// All checks use item NAMES (not IDs) to match DB items

export const TRIAL_DURATION_MS = 48 * 60 * 60 * 1000; // 48 hours

// Trial app skin names (must match Item.name in DB)
export const TRIAL_THEME_NAMES = ["Frost", "Spectrum"];

// Trial board IDs (maps to frontend board selection IDs)
export const TRIAL_BOARD_IDS = ["frost", "spectrum"];

// Static app skin items (used for non-authenticated users)
export const STATIC_APP_SKIN_ITEMS = [
  { id: 1, name: "Blaze",    type: "THEME", price: 0,   isPaid: false, isTrialAccessible: false },
  { id: 2, name: "Pulse",    type: "THEME", price: 5.0, isPaid: true,  isTrialAccessible: false },
  { id: 3, name: "Aura",     type: "THEME", price: 5.0, isPaid: true,  isTrialAccessible: false },
  { id: 4, name: "Nova",     type: "THEME", price: 5.0, isPaid: true,  isTrialAccessible: false },
  { id: 5, name: "Frost",    type: "THEME", price: 5.0, isPaid: true,  isTrialAccessible: true  },
  { id: 6, name: "Spectrum", type: "THEME", price: 5.0, isPaid: true,  isTrialAccessible: true  },
];

// Static board skin items (used for non-authenticated users)
export const STATIC_BOARD_SKIN_ITEMS = [
  { id: "inferno",  name: "Blaze Board",    type: "BOARD_SKIN", price: 0,   isPaid: false, isFree: true,  isTrialAccessible: false },
  { id: "frost",    name: "Frost Board",    type: "BOARD_SKIN", price: 5.0, isPaid: true,  isFree: false, isTrialAccessible: true  },
  { id: "spectrum", name: "Spectrum Board", type: "BOARD_SKIN", price: 5.0, isPaid: true,  isFree: false, isTrialAccessible: true  },
  { id: "none",     name: "Default",        type: "BOARD_SKIN", price: 0,   isPaid: false, isFree: true,  isTrialAccessible: false },
];

/**
 * Check if a user's trial is still active
 * @param {string|Date} trialStart - When the user's trial started
 * @returns {boolean}
 */
export function isTrialActive(trialStart) {
  if (!trialStart) return false;
  const start = new Date(trialStart);
  const now = new Date();
  return now.getTime() - start.getTime() < TRIAL_DURATION_MS;
}

/**
 * Get when the trial expires
 * @param {string|Date} trialStart - When the user's trial started
 * @returns {Date}
 */
export function getTrialExpiresAt(trialStart) {
  const start = new Date(trialStart);
  return new Date(start.getTime() + TRIAL_DURATION_MS);
}

/**
 * Get time remaining on trial in ms
 * @param {string|Date} trialStart - When the user's trial started
 * @returns {number} ms remaining, 0 if expired
 */
export function getTrialTimeRemaining(trialStart) {
  if (!trialStart) return 0;
  const expiresAt = getTrialExpiresAt(trialStart);
  const remaining = expiresAt.getTime() - Date.now();
  // Cap at TRIAL_DURATION_MS so timer never shows more than 48h
  // (can happen if trialStart was set in the future, e.g. via DB migration)
  return Math.min(Math.max(0, remaining), TRIAL_DURATION_MS);
}

/**
 * Check if a theme name is part of the trial
 * @param {string} themeName - Theme name (e.g., "Frost", "Spectrum")
 * @returns {boolean}
 */
export function isThemeInTrial(themeName) {
  return TRIAL_THEME_NAMES.includes(themeName);
}

/**
 * Check if a board ID is part of the trial
 * @param {string} boardId - Board ID (e.g., "inferno", "frost", "spectrum")
 * @returns {boolean}
 */
export function isBoardInTrial(boardId) {
  return TRIAL_BOARD_IDS.includes(boardId);
}
